import React, { useEffect, useRef } from 'react';
import { Apple, Calendar, ScanLine, MessageSquare, CalendarPlus, ArrowRight, CheckCircle2 } from 'lucide-react';

// ============================================================================
//  MEMBER APP SECTION — uses the REAL ClubAccess App Store marketing image.
//  • External Apple App Store URL kept EXACTLY as specified by the client.
//  • Self-hosted ClubAccess marketing screenshots (/img/clubaccess-app-preview.webp).
//  • Premium large red CTA button.
//  • Section ID, anchor, test IDs, and parallax tilt preserved.
// ============================================================================

const APP_STORE_URL = 'https://apps.apple.com/us/app/myclubaccess/id6498148220';
const PREVIEW_IMG   = '/img/clubaccess-app-preview.webp';

function AppPreview({ previewRef }) {
  return (
    <div
      ref={previewRef}
      className="relative w-full max-w-[560px] mx-auto"
      style={{
        transform: 'perspective(1600px) rotateY(8deg) rotateX(-4deg)',
        transformStyle: 'preserve-3d',
        transition: 'transform 0.3s ease-out',
        willChange: 'transform',
      }}
    >
      {/* Red glow under image */}
      <div
        aria-hidden
        className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-[80%] h-16 bg-red-600/30 blur-3xl rounded-full"
      />

      {/* Image card */}
      <div
        className="relative rounded-3xl overflow-hidden border border-red-600/25 bg-zinc-950/60 shadow-[0_30px_90px_-20px_rgba(0,0,0,0.85),0_0_0_1px_rgba(239,43,43,0.12)]"
        style={{ transform: 'translateZ(20px)' }}
      >
        <img
          src={PREVIEW_IMG}
          alt="ClubAccess app screenshots — Hub, Class Pack, Club Check-in, and Yoga booking screens"
          loading="lazy"
          decoding="async"
          width="1024"
          height="1024"
          className="block w-full h-auto"
          style={{
            // Radial fade so the bright orange image edges blend into the
            // dark red/black section background instead of hard-cutting.
            // Repeated as `WebkitMaskImage` for older WebKit / iOS Safari.
            maskImage: 'radial-gradient(ellipse 85% 80% at center, black 55%, transparent 100%)',
            WebkitMaskImage: 'radial-gradient(ellipse 85% 80% at center, black 55%, transparent 100%)',
            // Tone the orange marketing background toward red and slightly
            // desaturate so it harmonises with the FitnessPro red/black palette.
            // GPU-composited; degrades gracefully (image just shows full
            // orange if `filter` is unsupported — no broken state).
            filter: 'hue-rotate(-20deg) saturate(0.85)',
            WebkitFilter: 'hue-rotate(-20deg) saturate(0.85)',
          }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            const fb = e.currentTarget.nextElementSibling;
            if (fb && fb.classList.contains('app-preview-fallback')) {
              fb.style.opacity = '1';
            }
          }}
        />
        {/* Graceful fallback — keeps the card looking intentional if image
            ever fails to load. */}
        <div
          aria-hidden
          className="app-preview-fallback absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-zinc-900 to-black opacity-0 transition-opacity duration-300 pointer-events-none p-8 text-center"
        >
          <Apple className="w-12 h-12 text-red-500" />
          <div className="mt-4 font-display text-2xl text-white">MyClubAccess</div>
          <div className="text-[11px] tracking-[0.3em] text-zinc-500 mt-1">FREE ON THE APP STORE</div>
        </div>
      </div>

      {/* Small floating "App Store" pill — premium proof element */}
      <div
        aria-hidden
        className="absolute -left-4 -top-3 sm:-left-6 sm:-top-4 rounded-full bg-black/90 border border-red-600/40 px-3 py-1.5 flex items-center gap-1.5 shadow-[0_8px_30px_rgba(239,43,43,0.35)] backdrop-blur"
        style={{ transform: 'translateZ(60px)' }}
      >
        <Apple className="w-3 h-3 text-white" />
        <span className="text-[10px] text-white font-semibold tracking-wide">App Store</span>
      </div>
      <div
        aria-hidden
        className="absolute -right-3 -bottom-4 sm:-right-6 sm:-bottom-5 rounded-full bg-black/90 border border-red-600/40 px-3 py-1.5 flex items-center gap-1.5 shadow-[0_8px_30px_rgba(239,43,43,0.35)] backdrop-blur"
        style={{ transform: 'translateZ(60px)' }}
      >
        <CheckCircle2 className="w-3 h-3 text-red-500" />
        <span className="text-[10px] text-white font-semibold tracking-wide">Free Download</span>
      </div>
    </div>
  );
}

export default function MemberApp() {
  const containerRef = useRef(null);
  const previewRef = useRef(null);

  // Parallax tilt — desktop-only, RAF-coalesced, direct DOM writes.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.matchMedia('(pointer: coarse)').matches) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const container = containerRef.current;
    const node = previewRef.current;
    if (!container || !node) return undefined;
    let rect = container.getBoundingClientRect();
    const cacheRect = () => { rect = container.getBoundingClientRect(); };
    let raf = 0;
    let lx = 0;
    let ly = 0;
    const apply = () => {
      raf = 0;
      const x = (lx - rect.left) / rect.width - 0.5;
      const y = (ly - rect.top) / rect.height - 0.5;
      const rotX = -4 - y * 8;
      const rotY = 8 + x * 10;
      node.style.transform = `perspective(1600px) rotateY(${rotY}deg) rotateX(${rotX}deg)`;
    };
    const onMove = (e) => {
      lx = e.clientX;
      ly = e.clientY;
      if (!raf) raf = requestAnimationFrame(apply);
    };
    container.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('scroll', cacheRect, { passive: true });
    window.addEventListener('resize', cacheRect, { passive: true });
    return () => {
      container.removeEventListener('pointermove', onMove);
      window.removeEventListener('scroll', cacheRect);
      window.removeEventListener('resize', cacheRect);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      id="app"
      ref={containerRef}
      className="relative py-20 sm:py-28 lg:py-32 bg-black overflow-hidden"
    >
      {/* Ambient background */}
      <div className="absolute inset-0 grid-bg opacity-20" aria-hidden />
      <div className="absolute top-0 right-0 w-1/2 h-full bg-red-600/8 blur-[90px] rounded-full" aria-hidden />
      <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-red-600/5 blur-[120px] rounded-full" aria-hidden />
      <div
        aria-hidden
        className="absolute top-0 left-1/2 -translate-x-1/2 h-[2px] w-1/3 bg-gradient-to-r from-transparent via-red-500/60 to-transparent"
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* ============== LEFT COLUMN ============== */}
        <div className="reveal-left order-2 lg:order-1">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-[2px] w-10 bg-red-600" />
            <span className="text-xs tracking-[0.4em] text-red-500 font-semibold">MEMBER APP</span>
          </div>

          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            MANAGE YOUR FITNESSPRO MEMBERSHIP <span className="text-gradient-red">FROM YOUR PHONE</span>
          </h2>

          <p className="mt-6 text-zinc-400 text-base leading-relaxed max-w-md">
            Book classes, check in at the door, view gym updates, and stay connected with FitnessPro Galt through the MyClubAccess member app.
          </p>

          {/* Feature cards */}
          <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-4 max-w-lg">
            {[
              { Icon: Calendar,      title: 'Book Classes',     desc: 'Reserve Zumba, cardio, strength, and training sessions.' },
              { Icon: ScanLine,      title: 'Mobile Check-In',  desc: 'Use your membership barcode at the door.' },
              { Icon: MessageSquare, title: 'Gym Messages',     desc: 'Get important updates straight from the team.' },
              { Icon: CalendarPlus,  title: 'Sync Schedule',    desc: 'Add reserved sessions to your phone calendar.' },
            ].map(({ Icon, title, desc }) => (
              <div
                key={title}
                data-cursor="hover"
                className="group p-4 rounded-2xl bg-zinc-950 border border-zinc-900 hover:border-red-600/50 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-red-600/10 border border-red-600/30 flex items-center justify-center group-hover:bg-red-600 transition-colors duration-300">
                  <Icon className="w-5 h-5 text-red-500 group-hover:text-white transition-colors duration-300" />
                </div>
                <div className="mt-3 font-semibold text-white text-sm">{title}</div>
                <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>

          {/* PRIMARY CTA — premium red pill */}
          <div className="mt-10">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="member-app-appstore-btn"
              data-cursor="hover"
              aria-label="Download MyClubAccess on the App Store"
              className="btn-shimmer group inline-flex items-center gap-3 px-8 py-4 sm:py-5 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white rounded-full text-xs sm:text-sm tracking-[0.25em] font-bold shadow-[0_15px_40px_rgba(239,43,43,0.45),0_0_0_1px_rgba(239,43,43,0.3)] hover:shadow-[0_20px_60px_rgba(239,43,43,0.65),0_0_0_1px_rgba(239,43,43,0.5)] transition-all duration-300"
            >
              <Apple className="w-5 h-5 flex-shrink-0" />
              <span>DOWNLOAD ON THE APP STORE</span>
              <ArrowRight className="w-4 h-4 flex-shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>

          <div className="mt-6 flex items-center gap-2 text-[10px] tracking-[0.3em] text-zinc-500">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
            </span>
            FREE TO DOWNLOAD · iOS
          </div>
        </div>

        {/* ============== RIGHT COLUMN — REAL CLUBACCESS APP PREVIEW ============== */}
        <div className="relative reveal-right order-1 lg:order-2 flex items-center justify-center min-h-[360px] sm:min-h-[440px] lg:min-h-[520px]">
          <AppPreview previewRef={previewRef} />
        </div>
      </div>
    </section>
  );
}
